/* Pulse product page — the behaviour the two acts need, in plain DOM.
 *
 * Ported from the Hydrogen sources:
 *   pdp/ProductGallery.tsx + pdp/Lightbox.tsx  -> gallery dots and the enlarged view
 *   pdp/BuyPanel.tsx                           -> size selection, button state, gender toggle
 *   pdp/ShoeFilm.tsx                           -> the film, gated on visibility
 *   pdp/TechTiles.tsx + useRail.ts             -> the tile rail's arrows, drag, dots and range
 *   Stories.tsx                                -> roundels and the full-screen player
 *   routes/preview.product.tsx                 -> the mobile buy bar
 *
 * Two rules carried over from the sources, because both were bought with real
 * bugs. Video is preload="none" and only ever plays while it is on screen:
 * eager video was the main performance trap on the homepage. And nothing in
 * here ever writes a variant id — the radio the customer checks IS name="id",
 * so a script cannot make the visible selection and the posted value disagree.
 */
(function () {
  if (window.__pulse_product) return;
  window.__pulse_product = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  /* --- women / men ---------------------------------------------------------
   * The source kept two size runs and swapped between them. Here every real
   * variant is rendered once with BOTH readings of its label, and this only
   * chooses which one leads. The selected size therefore stays valid across a
   * switch, which is the whole reason the port works this way.
   */
  function applyGender(g) {
    $$('[data-pulse-gender]').forEach(function (el) {
      el.setAttribute('data-pulse-gender', g);
    });
    $$('[data-pulse-genderseg] [data-gender]').forEach(function (btn) {
      var on = btn.getAttribute('data-gender') === g;
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    /* The size grid switched systems in CSS. A <select> cannot: an <option> has
       no styleable children, so the label text is rewritten here from the two
       readings the snippet rendered as data attributes. The option's VALUE is
       never touched, which is why the selected size survives the switch. */
    /* Keep the hidden cart-line properties in step with the toggle, so whatever
       is added carries the scale it was chosen in AND the size in words. */
    syncScaleInputs(g);

    var attr = g === 'men' ? 'data-text-m' : 'data-text-w';
    $$('[data-pulse-sizeselect] option[data-pulse-sizeopt]').forEach(function (o) {
      var t = o.getAttribute(attr);
      if (t) o.textContent = t;
    });
    /* The <h1> cannot use the CSS switch either, for a different reason: both
       readings would sit in its text, so the heading reads "Women's Men's Flow 2
       White" to anything that does not run CSS. One span, rewritten from the same
       two attributes. */
    $$('[data-pulse-genderlabel]').forEach(function (el) {
      var t = el.getAttribute(attr);
      if (t) el.textContent = t;
    });
    /* The size chart's lead column is ONE reading too (audit SEO-6): the CSS
       pair left both numbers in the DOM, so a text extractor read "5" and "4"
       as "54" down the whole chart. Rewritten from the row's own data-usw /
       data-usm, which markRow() and focusRow() already key on. */
    $$('[data-pulse-chart-scalehead]').forEach(function (th) {
      var t = th.getAttribute(attr);
      if (t) th.textContent = t;
    });
    $$('[data-psg-row] [data-pulse-chart-size]').forEach(function (th) {
      var tr = th.closest('[data-psg-row]');
      var v = tr && tr.getAttribute(g === 'men' ? 'data-usm' : 'data-usw');
      /* The cell carries a gender word beside the number (2026-09-04); write
         the number SPAN when it exists, or the word is wiped on every switch. */
      var num = th.querySelector('[data-pulse-chart-num]');
      if (v) (num || th).textContent = v;
      var gw = th.querySelector('[data-pulse-chart-gword]');
      var gt = gw && gw.getAttribute(attr);
      if (gw && gt) gw.textContent = gt;
    });
  }

  /* The two hidden inputs in the product form. `_scale` is what the CART reads
     back to keep a line in the scale it was chosen in; `Size` has no underscore,
     so it SHOWS on the checkout line, the order and the confirmation email.

     Both have to follow the size select as well as the women's/men's toggle --
     a customer picks the scale once and the size after it. The Size input is
     DISABLED while nothing is chosen, because a disabled input is not
     serialised, and an empty property would otherwise ride along on the add. */
  function syncScaleInputs(g) {
    var scale = g === 'men' ? 'm' : 'w';
    var sel = $('[data-pulse-sizeselect]');
    var opt = sel && sel.options[sel.selectedIndex];
    var label = opt ? opt.getAttribute(scale === 'm' ? 'data-label-m' : 'data-label-w') : '';
    $$('[data-pulse-scale-input]').forEach(function (el) { el.value = scale; });
    $$('[data-pulse-size-label-input]').forEach(function (el) {
      el.value = label || '';
      el.disabled = !label;
    });
  }

  function initGender() {
    /* Storage and the default now live in window.PulseSizeScale
       (snippets/pulse-size-scale.liquid) -- ONE key across the PDP, the
       collection wall and the cart, where there used to be two that could
       disagree. This file still owns the DOM work, because only it knows about
       the segment buttons, the <h1> label and the <option> text.

       The button handler ONLY calls set(); applyGender runs from the onChange
       subscription. Doing both would re-enter this handler from inside itself. */
    applyGender(window.PulseSizeScale.gender());
    window.PulseSizeScale.onChange(function (v) {
      applyGender(v === 'm' ? 'men' : 'women');
    });

    $$('[data-pulse-genderseg]').forEach(function (seg) {
      if (seg.__pulse) return;
      seg.__pulse = true;
      seg.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-gender]');
        if (!btn) return;
        window.PulseSizeScale.set(btn.getAttribute('data-gender'));
      });
    });
  }

  /* --- size grid and buy button -------------------------------------------- */

  function initBuy(root) {
    /* Two shapes of the same control. The PDP is now a <select> whose <option>s
       carry the per-size data; the older radio grid (pulse-pdp-main.liquid) is
       still read the same way. Both expose .disabled, and .selected/.checked is
       the only difference, so `isOn` is the whole adapter. */
    var sizeSelect = $('[data-pulse-sizeselect]', root);
    var radios = sizeSelect
      ? $$('option[data-pulse-sizeopt]', sizeSelect)
      : $$('[data-pulse-size]', root);
    var atc = $('[data-pulse-atc]', root);
    var atcLabel = atc && (atc.querySelector('[data-pulse-atc-label]') || atc);
    var low = $('[data-pulse-low]', root);
    var left = $('[data-pulse-left]', root);
    /* Pre-order line. `preForced` remembers the section-level whole-product
       override so a per-size state never hides a deliberately forced pre-order. */
    var pre = $('[data-pulse-preorder]', root);
    var preShips = pre && pre.querySelector('[data-pulse-preorder-ships]');
    var preForced = !!pre && !pre.hidden;
    var preShipsDefault = preShips ? preShips.textContent : '';
    if (!radios.length || !atc) return;

    function isOn(r) {
      return sizeSelect ? r.selected : r.checked;
    }

    /* Price line. Liquid paints it for the first variant; this keeps the price,
       the struck compare-at and the chip in step with the size the customer
       picks, per market: a stocked size shows the plain price, a pre-order size
       with a compare-at shows the strike and "Pre-order price", a clearance
       product shows the strike and "Clearance" (Andre, 2026-09-03). No Save
       pill and no raspberry price: the strike is the only sale signal. Every element is always in the DOM and only [hidden] moves;
       nothing is created here. A pre-order verdict that is not per size
       (data-preorder-forced) is never undone by a size's own state. */
    var priceBox = $('[data-pulse-pricebox]', root);
    function paintPrice(chosen) {
      if (!priceBox || !chosen || !chosen.hasAttribute('data-price')) return;
      var priceEl = priceBox.querySelector('[data-pulse-price]');
      var wasEl = priceBox.querySelector('[data-pulse-price-was]');
      var badgeEl = priceBox.querySelector('[data-pulse-price-badge]');
      var compare = chosen.getAttribute('data-compare') || '';
      var isPre = priceBox.hasAttribute('data-preorder-forced') ||
        chosen.getAttribute('data-state') === 'preorder';
      if (priceEl) priceEl.textContent = chosen.getAttribute('data-price');
      if (wasEl) {
        wasEl.textContent = compare;
        wasEl.hidden = !compare;
      }
      if (badgeEl) {
        var flag = '';
        if (isPre) {
          flag = compare
            ? priceBox.getAttribute('data-flag-preorder-price')
            : priceBox.getAttribute('data-flag-preorder');
        } else {
          /* Clearance: the chosen size's own discount (data-pct, from the picker)
             in the "[pct]% off" pattern, else the "Up to N% off" / "Clearance" word
             Liquid painted. Empty on anything that is not clearance. */
          var pct = chosen.getAttribute('data-pct') || '';
          var offPattern = priceBox.getAttribute('data-flag-off') || '';
          flag = pct && offPattern ? offPattern.replace('[pct]', pct) : (priceBox.getAttribute('data-flag-clearance') || '');
        }
        badgeEl.textContent = flag || '';
        badgeEl.hidden = !flag;
      }
    }

    var anyAvailable = radios.some(function (r) {
      return !r.disabled;
    });

    function sync() {
      var chosen = null;
      radios.forEach(function (r) {
        var on = isOn(r);
        var cell = r.closest ? r.closest('.pdp-size') : null;
        if (cell) cell.classList.toggle('is-on', on);
        if (on) chosen = r;
      });

      paintPrice(chosen);

      /* The selling plan travels only with a pre-order size (see the hidden
         input in pulse-product.liquid). A disabled input is not serialised. */
      var planInput = $('[data-pulse-plan-input]', root);
      if (planInput) {
        var plan = chosen && chosen.getAttribute('data-state') === 'preorder'
          ? (chosen.getAttribute('data-plan') || '') : '';
        planInput.value = plan;
        planInput.disabled = !plan;
      }

      if (!anyAvailable) {
        atc.disabled = true;
        atc.classList.remove('is-preorder');
        atcLabel.textContent = atc.getAttribute('data-label-soldout');
      } else if (chosen) {
        atc.disabled = false;
        var price = chosen.getAttribute('data-price');
        /* data-state is rendered per size, per market, by pulse-size-picker.liquid.
           A pre-order size is buyable, it just says so on the button. */
        var isPreorder = chosen.getAttribute('data-state') === 'preorder';
        var base = isPreorder
          ? (atc.getAttribute('data-label-preorder') || atc.getAttribute('data-label-idle'))
          : atc.getAttribute('data-label-idle');
        atcLabel.textContent = base;   /* no price on the button (Andre, 2026-09-02) */
        /* Red button, so the customer cannot mistake a pre-order for a normal buy. */
        atc.classList.toggle('is-preorder', isPreorder);
      } else {
        atc.disabled = true;
        atc.classList.remove('is-preorder');
        atcLabel.textContent = atc.getAttribute('data-label-choose');
      }

      if (pre) {
        var isPre = !!chosen && chosen.getAttribute('data-state') === 'preorder';
        var ships = chosen ? chosen.getAttribute('data-ships') : '';
        pre.hidden = !(preForced || isPre);
        if (preShips) preShips.textContent = ships || preShipsDefault;
      }

      if (left) {
        // Liquid decides the qualitative message; exact stock never reaches the browser.
        var lt = chosen ? chosen.getAttribute('data-stock-message') || '' : '';
        left.textContent = lt;
        left.hidden = !lt;
      }

      if (low) {
        low.hidden = !(chosen && chosen.getAttribute('data-low-stock') === 'true');
      }
    }

    if (sizeSelect) {
      sizeSelect.addEventListener('change', sync);
      /* The size just changed, so the words on the cart-line property must too. */
      sizeSelect.addEventListener('change', function () {
        syncScaleInputs(window.PulseSizeScale.gender());
      });
    } else {
      radios.forEach(function (r) {
        r.addEventListener('change', sync);
      });
    }
    sync();
  }

  /* --- gallery: dots, and the enlarged view -------------------------------- */

  function initGallery(root) {
    var track = $('[data-pulse-gallery]', root);
    if (!track || track.__pulse) return;
    track.__pulse = true;

    var slides = $$('.pdp-gallery__slide', track);
    var dots = $$('.pdp-gallery__dot', root);
    var current = 0;

    /* Desktop stacks vertically and never scrolls this box, so the clientWidth
       maths only ever runs on the mobile layout. */
    var frame = 0;
    track.addEventListener(
      'scroll',
      function () {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = 0;
          if (track.scrollWidth <= track.clientWidth) return;
          var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          if (i === current) return;
          current = i;
          dots.forEach(function (d, n) {
            d.classList.toggle('is-on', n === i);
          });
        });
      },
      { passive: true }
    );

    var shots = slides.map(function (s) {
      return {
        full: s.getAttribute('data-full'),
        alt: s.getAttribute('data-alt') || '',
      };
    });

    slides.forEach(function (s, i) {
      s.addEventListener('click', function () {
        openLightbox(shots, i);
      });
    });
  }

  /* The enlarged view. Light rather than the usual near-black scrim: this store
     has no dark theme by decision, and the shoes are photographed on a near-white
     ground, so dropping them onto black inverts the whole product. Arrow keys and
     Escape work, because a viewer you can only leave by hunting for an X is one
     people avoid opening twice. */
  function openLightbox(shots, index) {
    if (!shots.length) return;
    var i = index;
    /* Where focus came from, so dismiss can hand it back (audit T-23). */
    var opener = document.activeElement;

    var lb = document.createElement('div');
    lb.className = 'lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', shots[i].alt);

    var scrim = document.createElement('button');
    scrim.type = 'button';
    scrim.className = 'lb__scrim';
    scrim.setAttribute('aria-label', 'Close');

    var stage = document.createElement('figure');
    stage.className = 'lb__stage';
    var img = document.createElement('img');
    stage.appendChild(img);

    /* Zoom and pan. The lightbox master is the full-resolution photograph
       (4928x3264), so 100% scale IS the detail ceiling: scrolling or pinching
       past it would only blur, so the range is [fit .. native pixels]. All
       coordinates are viewport coordinates because the stage is fixed inset:0. */
    var scale = 1, fit = 1, tx = 0, ty = 0;
    function apply() {
      img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
      lb.classList.toggle('is-zoomed', scale > fit * 1.001);
    }
    function fitNow() {
      var nw = img.naturalWidth, nh = img.naturalHeight;
      if (!nw) return;
      fit = Math.min(window.innerWidth / nw, window.innerHeight / nh, 1);
      scale = fit;
      tx = (window.innerWidth - nw * scale) / 2;
      ty = (window.innerHeight - nh * scale) / 2;
      apply();
    }
    function clampScale(v) {
      return Math.max(fit, Math.min(Math.max(1, fit), v));
    }
    function zoomAt(cx, cy, f) {
      var ns = clampScale(scale * f);
      if (ns === scale) return;
      tx = cx - (cx - tx) * (ns / scale);
      ty = cy - (cy - ty) * (ns / scale);
      scale = ns;
      apply();
    }
    img.onload = fitNow;
    img.addEventListener('dragstart', function (e) { e.preventDefault(); });
    lb.addEventListener('wheel', function (e) {
      e.preventDefault();
      zoomAt(e.clientX, e.clientY, e.deltaY < 0 ? 1.18 : 1 / 1.18);
    }, { passive: false });

    /* One pointer pans when zoomed, two pinch, a clean tap or click toggles
       between fit and 100% at the point touched. Pointer events cover mouse
       and touch with one code path. */
    var pts = new Map(), moved = false, panStart = null, pinchStart = null;
    img.addEventListener('pointerdown', function (e) {
      e.preventDefault();
      img.setPointerCapture(e.pointerId);
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      moved = false;
      if (pts.size === 1) {
        panStart = { x: e.clientX - tx, y: e.clientY - ty };
      } else if (pts.size === 2) {
        var a = Array.from(pts.values());
        pinchStart = { d: Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y), s: scale };
        panStart = null;
      }
    });
    img.addEventListener('pointermove', function (e) {
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      var a = Array.from(pts.values());
      if (pts.size === 2 && pinchStart) {
        var d = Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y);
        if (Math.abs(d - pinchStart.d) > 4) moved = true;
        var cx = (a[0].x + a[1].x) / 2, cy = (a[0].y + a[1].y) / 2;
        var ns = clampScale(pinchStart.s * d / pinchStart.d);
        tx = cx - (cx - tx) * (ns / scale);
        ty = cy - (cy - ty) * (ns / scale);
        scale = ns;
        apply();
      } else if (pts.size === 1 && panStart && scale > fit * 1.001) {
        var nx = e.clientX - panStart.x, ny = e.clientY - panStart.y;
        if (Math.abs(nx - tx) + Math.abs(ny - ty) > 3) moved = true;
        tx = nx; ty = ny;
        apply();
      }
    });
    function endPointer(e) {
      if (!pts.has(e.pointerId)) return;
      pts.delete(e.pointerId);
      pinchStart = null;
      if (pts.size === 1) {
        var a = Array.from(pts.values())[0];
        panStart = { x: a.x - tx, y: a.y - ty };
      } else if (pts.size === 0) {
        if (!moved && e.type === 'pointerup') {
          if (scale > fit * 1.001) fitNow();
          else zoomAt(e.clientX, e.clientY, Math.max(1, fit) / scale);
        }
        panStart = null;
      }
    }
    img.addEventListener('pointerup', endPointer);
    img.addEventListener('pointercancel', endPointer);
    window.addEventListener('resize', fitNow);

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'lb__close';
    close.textContent = 'Close';

    lb.appendChild(scrim);
    lb.appendChild(stage);
    lb.appendChild(close);

    var prev, next, thumbs;
    if (shots.length > 1) {
      prev = document.createElement('button');
      prev.type = 'button';
      prev.className = 'lb__arrow lb__arrow--prev';
      prev.setAttribute('aria-label', 'Previous image');
      prev.innerHTML = '&#8249;';

      next = document.createElement('button');
      next.type = 'button';
      next.className = 'lb__arrow lb__arrow--next';
      next.setAttribute('aria-label', 'Next image');
      next.innerHTML = '&#8250;';

      thumbs = document.createElement('div');
      thumbs.className = 'lb__thumbs';
      shots.forEach(function (shot, n) {
        var t = document.createElement('button');
        t.type = 'button';
        t.className = 'lb__thumb';
        t.setAttribute('aria-label', shot.alt);
        var ti = document.createElement('img');
        ti.src = shot.full;
        ti.alt = '';
        ti.loading = 'lazy';
        t.appendChild(ti);
        t.addEventListener('click', function () {
          show(n);
        });
        thumbs.appendChild(t);
      });

      prev.addEventListener('click', function () {
        show(i - 1);
      });
      next.addEventListener('click', function () {
        show(i + 1);
      });

      lb.appendChild(prev);
      lb.appendChild(next);
      lb.appendChild(thumbs);
    }

    function show(n) {
      i = Math.max(0, Math.min(shots.length - 1, n));
      lb.classList.remove('is-zoomed');
      img.style.transform = '';
      img.src = shots[i].full;
      img.alt = shots[i].alt;
      lb.setAttribute('aria-label', shots[i].alt);
      if (prev) prev.disabled = i === 0;
      if (next) next.disabled = i >= shots.length - 1;
      if (thumbs) {
        $$('.lb__thumb', thumbs).forEach(function (t, n2) {
          t.classList.toggle('is-current', n2 === i);
          t.setAttribute('aria-current', n2 === i ? 'true' : 'false');
        });
      }
    }

    function dismiss() {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', fitNow);
      lb.remove();
      if (typeof removeTrapFocus === 'function') removeTrapFocus(opener && opener.focus ? opener : null);
      else if (opener && opener.focus) opener.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') dismiss();
      if (e.key === 'ArrowRight') show(i + 1);
      if (e.key === 'ArrowLeft') show(i - 1);
      if (e.key === '+' || e.key === '=') zoomAt(window.innerWidth / 2, window.innerHeight / 2, 1.25);
      if (e.key === '-' || e.key === '_') zoomAt(window.innerWidth / 2, window.innerHeight / 2, 1 / 1.25);
      if (e.key === '0') fitNow();
    }

    scrim.addEventListener('click', dismiss);
    close.addEventListener('click', dismiss);

    var previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    show(i);
    document.body.appendChild(lb);
    close.focus();
    if (typeof trapFocus === 'function') trapFocus(lb, close);
  }

  /* --- video that only ever plays while it is on screen -------------------- */

  function gateVideo(el) {
    if (!el || el.__pulse) return;
    el.__pulse = true;
    var io = new IntersectionObserver(
      function (entries) {
        if (entries[0].isIntersecting) {
          /* The poster rides on data-poster and is set here, so ten 80KB
             frames are not fetched on every PDP load (audit T-20). Under a
             reduced-motion preference the poster frame is the whole experience. */
          if (!el.poster && el.dataset.poster) el.poster = el.dataset.poster;
          if (reduced.matches) return;
          var p = el.play();
          if (p && p.catch) p.catch(function () {});
        } else {
          el.pause();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(el);
  }

  /* --- the technology tile rail -------------------------------------------
   * The rail is plain CSS scroll-snap. This adds only what a native scroll
   * container lacks with a mouse (arrows, grab-drag) and reports where the rail
   * is so the dots, the count and the edge fade can follow.
   */
  function initRail(root) {
    var rail = $('[data-pulse-rail]', root);
    if (!rail || rail.__pulse) return;
    rail.__pulse = true;

    var wrap = rail.parentElement;
    var prev = $('[data-pulse-rail-prev]', root);
    var next = $('[data-pulse-rail-next]', root);
    var dots = $$('[data-pulse-rail-dots] button', root);
    var count = $('[data-pulse-rail-count]', root);
    var cards = Array.prototype.slice.call(rail.children);
    var n = cards.length;
    var target = 0;
    var lastGo = 0;

    function pad(v) {
      return String(v).length < 2 ? '0' + v : String(v);
    }

    /* First and last card at least half on screen. Start alignment shows several
       at a time and the last few share the final frame, so a single "current
       card" model breaks down at the end; a visible RANGE is both true and
       clickable everywhere. */
    function visible() {
      var box = rail.getBoundingClientRect();
      var seen = [];
      cards.forEach(function (c, i) {
        var r = c.getBoundingClientRect();
        var shown = Math.min(r.right, box.right) - Math.max(r.left, box.left);
        if (shown >= r.width / 2) seen.push(i);
      });
      return seen.length ? [seen[0], seen[seen.length - 1]] : [0, 0];
    }

    function nearest() {
      var left = rail.getBoundingClientRect().left;
      var best = 0;
      var bestD = Infinity;
      cards.forEach(function (c, i) {
        var d = Math.abs(c.getBoundingClientRect().left - left);
        if (d < bestD) {
          bestD = d;
          best = i;
        }
      });
      return best;
    }

    function read() {
      var range = visible();
      var max = rail.scrollWidth - rail.clientWidth;
      rail.classList.toggle('is-scrolled', rail.scrollLeft > 1 && rail.scrollLeft < max - 1);
      rail.classList.toggle('is-end', rail.scrollLeft >= max - 1 && max > 0);
      if (prev) prev.disabled = rail.scrollLeft <= 1;
      if (next) next.disabled = max <= 0 || rail.scrollLeft >= max - 1;
      dots.forEach(function (d, i) {
        var on = i >= range[0] && i <= range[1];
        d.classList.toggle('is-on', on);
        d.setAttribute('aria-current', on ? 'true' : 'false');
      });
      if (count) count.textContent = pad(range[0] + 1) + '–' + pad(range[1] + 1) + ' / ' + n;
      /* Re-sync the arrows' target to reality, but only once any programmatic
         scroll has had time to land. */
      if (performance.now() - lastGo > 700) target = nearest();
    }

    function go(i) {
      var card = cards[Math.max(0, Math.min(n - 1, i))];
      if (!card) return;
      target = Math.max(0, Math.min(n - 1, i));
      lastGo = performance.now();
      rail.scrollTo({
        left: rail.scrollLeft + (card.getBoundingClientRect().left - rail.getBoundingClientRect().left),
        behavior: reduced.matches ? 'auto' : 'smooth',
      });
    }

    var frame = 0;
    rail.addEventListener(
      'scroll',
      function () {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = 0;
          read();
        });
      },
      { passive: true }
    );
    window.addEventListener('resize', read);

    if (prev) {
      prev.addEventListener('click', function () {
        go(target - 1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        go(target + 1);
      });
    }
    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        go(parseInt(d.getAttribute('data-index'), 10) || 0);
      });
    });

    /* Drag-to-scroll for MICE only; touch keeps the browser's own scrolling.
       `moved` suppresses the click that would otherwise follow a drag. */
    var drag = null;
    var moved = false;
    var snapWas = '';
    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse') return;
      drag = { x: e.clientX, left: rail.scrollLeft };
      moved = false;
    });
    rail.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x;
      if (Math.abs(dx) > 4) {
        if (!moved) {
          /* This rail is `scroll-snap-type: x mandatory`, and a scrollLeft
             write on a mandatory-snap container is re-snapped to the nearest
             snap point immediately. Without suspending snap the rail did not
             move at all until the drag passed half a cell, then jumped a whole
             cell (measured 2026-09-02: a 200px drag left scrollLeft on 0).
             The release below restores it, which is what settles the rail. */
          snapWas = rail.style.scrollSnapType;
          rail.style.scrollSnapType = 'none';
        }
        moved = true;
        if (!rail.hasPointerCapture(e.pointerId)) rail.setPointerCapture(e.pointerId);
      }
      if (moved) rail.scrollLeft = drag.left - dx;
    });
    function release(e) {
      drag = null;
      if (rail.hasPointerCapture(e.pointerId)) rail.releasePointerCapture(e.pointerId);
      if (moved) {
        rail.style.scrollSnapType = snapWas;
        var i = nearest();
        window.setTimeout(function () {
          go(i);
        }, 0);
      }
    }
    rail.addEventListener('pointerup', release);
    rail.addEventListener('pointercancel', release);
    rail.addEventListener(
      'click',
      function (e) {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      },
      true
    );

    if (wrap) read();
  }

  /* --- dance roundels and the full-screen player --------------------------- */

  function initStories(root) {
    var section = $('[data-pulse-stories]', root);
    if (!section || section.__pulse) return;
    section.__pulse = true;

    $$('.stories__ring video', section).forEach(gateVideo);

    var buttons = $$('[data-pulse-story]', section);
    var stories = buttons.map(function (b) {
      return {
        video: b.getAttribute('data-video'),
        poster: b.getAttribute('data-poster'),
        style: b.getAttribute('data-style') || '',
        handle: b.getAttribute('data-handle') || '',
      };
    });

    buttons.forEach(function (b, i) {
      b.addEventListener('click', function () {
        openPlayer(stories, i);
      });
    });

    initStoriesArrows(section);
  }

  /* The roundel row scrolls horizontally with no visible scrollbar, so past
     about six styles the rest are invisible with nothing to say so. These
     arrows are that affordance; CSS hides them on touch and under 768px. */
  function initStoriesArrows(section) {
    var row = $('[data-pulse-stories-row]', section);
    var prev = $('[data-pulse-stories-prev]', section);
    var next = $('[data-pulse-stories-next]', section);
    if (!row || !prev || !next) return;

    function step() {
      /* Just under a full pane, so the roundel at the edge stays half in view
         and reads as "there is more", rather than jumping a clean page. */
      return Math.max(160, Math.round(row.clientWidth * 0.8));
    }

    function sync() {
      /* 2px of slack: sub-pixel layout means scrollLeft rarely lands exactly
         on the maximum, and an arrow that never disables looks broken. */
      var max = row.scrollWidth - row.clientWidth;
      prev.disabled = row.scrollLeft <= 2;
      next.disabled = row.scrollLeft >= max - 2;
    }

    function go(dir) {
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      row.scrollTo({ left: row.scrollLeft + dir * step(), behavior: reduce ? 'auto' : 'smooth' });
    }

    prev.addEventListener('click', function () { go(-1); });
    next.addEventListener('click', function () { go(1); });
    row.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  function openPlayer(stories, index) {
    var i = index;
    /* Where focus came from, so dismiss can hand it back (audit T-23). */
    var opener = document.activeElement;

    var player = document.createElement('div');
    player.className = 'player';
    player.setAttribute('role', 'dialog');
    player.setAttribute('aria-modal', 'true');

    var scrim = document.createElement('button');
    scrim.type = 'button';
    scrim.className = 'player__scrim';
    scrim.setAttribute('aria-label', 'Close');

    var stage = document.createElement('div');
    stage.className = 'player__stage';

    var bars = document.createElement('div');
    bars.className = 'player__bars';
    bars.setAttribute('aria-hidden', 'true');
    var fills = stories.map(function () {
      var bar = document.createElement('span');
      bar.className = 'player__bar';
      var fill = document.createElement('span');
      bar.appendChild(fill);
      bars.appendChild(bar);
      return fill;
    });

    var video = document.createElement('video');
    video.className = 'player__video';
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.autoplay = true;

    var meta = document.createElement('div');
    meta.className = 'player__meta';
    var styleLine = document.createElement('p');
    styleLine.className = 'display text-[1.1rem]';
    /* The handle, as plain text. A credit, not a link (Andre 2026-08-21), so
       this stays a <p> and never becomes an <a>. */
    var handleLine = document.createElement('p');
    handleLine.className = 'player__dancer';
    meta.appendChild(styleLine);
    meta.appendChild(handleLine);

    /* Tap targets, deliberately unlabelled visually. */
    var half1 = document.createElement('button');
    half1.type = 'button';
    half1.className = 'player__half player__half--prev';
    half1.setAttribute('aria-label', 'Previous');
    var half2 = document.createElement('button');
    half2.type = 'button';
    half2.className = 'player__half player__half--next';
    half2.setAttribute('aria-label', 'Next');

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'player__close';
    close.setAttribute('aria-label', 'Close');
    close.textContent = 'Close';

    stage.appendChild(bars);
    stage.appendChild(video);
    stage.appendChild(meta);
    stage.appendChild(half1);
    stage.appendChild(half2);
    stage.appendChild(close);
    player.appendChild(scrim);
    player.appendChild(stage);

    function paint() {
      fills.forEach(function (f, n) {
        f.style.transform = 'scaleX(' + (n < i ? 1 : 0) + ')';
      });
    }

    function show(n) {
      /* Past the last one, we're done. */
      if (n >= stories.length) {
        dismiss();
        return;
      }
      i = Math.max(0, n);
      var s = stories[i];
      video.src = s.video;
      video.poster = s.poster;
      styleLine.textContent = s.style;
      handleLine.textContent = s.handle;
      player.setAttribute('aria-label', s.style);
      paint();
      video.currentTime = 0;
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
    }

    video.addEventListener('timeupdate', function () {
      if (!video.duration) return;
      fills[i].style.transform = 'scaleX(' + video.currentTime / video.duration + ')';
    });
    video.addEventListener('ended', function () {
      show(i + 1);
    });

    function dismiss() {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
      video.pause();
      player.remove();
      if (typeof removeTrapFocus === 'function') removeTrapFocus(opener && opener.focus ? opener : null);
      else if (opener && opener.focus) opener.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') dismiss();
      if (e.key === 'ArrowRight') show(i + 1);
      if (e.key === 'ArrowLeft') show(i - 1);
    }

    scrim.addEventListener('click', dismiss);
    close.addEventListener('click', dismiss);
    half1.addEventListener('click', function () {
      show(i - 1);
    });
    half2.addEventListener('click', function () {
      show(i + 1);
    });

    var previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    document.body.appendChild(player);
    show(i);
    close.focus();
    if (typeof trapFocus === 'function') trapFocus(player, close);
  }

  /* --- boot ---------------------------------------------------------------- */

  function init(scope) {
    var root = scope || document;
    initGender();
    initBuy(root);
    initGallery(root);
    initRail(root);
    initStories(root);
    $$('[data-pulse-film]', root).forEach(gateVideo);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* The theme editor swaps a section's HTML in place, so re-wire whatever landed. */
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();

/* Size chart window (Andre, 2026-08-18 20:30): show N rows, scroll for the rest,
   and open with the configured size in the middle of the window for the reading
   the customer has chosen (men 7, women 4 by default; women's run starts at 5, so
   a missing size opens at the top). Re-centres when the gender toggle changes. */
(function () {
  /* Same double-load guard as the accordions below: this file is included by
     both Act 1 and Act 2, and two copies of the toggle handler applied the
     scroll correction twice. */
  if (window.__pulse_chart) return;
  var det = document.querySelector('[data-pulse-chart]');
  if (!det) return;
  window.__pulse_chart = true;
  var scroll = det.querySelector('[data-pulse-chart-scroll]');
  var rows = Array.prototype.slice.call(det.querySelectorAll('tbody tr'));
  var visible = parseInt(det.getAttribute('data-visible-rows'), 10) || 8;
  function gender() {
    var root = document.querySelector('[data-pulse-gender]');
    return root && root.getAttribute('data-pulse-gender') === 'men' ? 'men' : 'women';
  }
  function layout() {
    if (!rows.length) return;
    var head = det.querySelector('thead');
    var rowH = rows[0].getBoundingClientRect().height || 36;
    var headH = head ? head.getBoundingClientRect().height : 0;
    scroll.style.maxHeight = Math.round(headH + rowH * visible) + 'px';
    var g = gender();
    var want = det.getAttribute(g === 'men' ? 'data-focus-men' : 'data-focus-women');
    var attr = g === 'men' ? 'data-usm' : 'data-usw';
    var idx = rows.findIndex(function (r) { return r.getAttribute(attr) === want; });
    if (idx < 0) { scroll.scrollTop = 0; return; }
    var target = idx - Math.floor(visible / 2);
    scroll.scrollTop = Math.max(0, target) * rowH;
  }
  /* Keep the summary line where it is on open, so the chart appears BELOW it
     (push down) rather than the page adjusting to keep the button below in view. */
  var summary = det.querySelector('summary');
  det.addEventListener('toggle', function () {
    if (!det.open) return;
    var before = summary.getBoundingClientRect().top;
    requestAnimationFrame(function () {
      layout();
      var after = summary.getBoundingClientRect().top;
      if (Math.abs(after - before) > 1) window.scrollBy(0, after - before);
    });
  });
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('[data-pulse-genderseg] [data-gender]')) setTimeout(function () { if (det.open) layout(); }, 30);
  });
})();

/* Accordions (Andre, 2026-08-25: "expand down, not up ... smooth and seamless").
 *
 * Native <details> keeps the keyboard and screen-reader behaviour; this only
 * takes over the visual open and close so the panel grows and shrinks instead
 * of snapping. Three things it has to get right:
 *
 *   - Expanding downwards. A panel that opens must never push its own heading
 *     off the top of the screen. Two mechanisms do that together: the browser's
 *     scroll anchoring is off (overflow-anchor: none in pulse-core.css), which
 *     is what made the page scroll itself up when content grew, and the summary
 *     line is measured before and after the toggle and any residual shift is
 *     cancelled. Same trick the size chart above already uses.
 *   - No flash on open. open=true and height=0 are set in the same task, so the
 *     full-height frame is never painted.
 *   - Closing at all. A <details> hides its content the instant open goes
 *     false, so the attribute is only dropped once the collapse has finished.
 */
(function () {
  /* Act 1 and Act 2 both pull this file in, so it runs twice on a product page.
     Without this guard every summary got two listeners and the second undid the
     first, leaving the panel exactly as it was. */
  if (window.__pulse_acc) return;
  var root = document.querySelector('[data-pulse-acc]');
  if (!root) return;
  window.__pulse_acc = true;

  var DUR = 340;
  var EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
  var reducedQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  Array.prototype.slice.call(root.querySelectorAll('details')).forEach(function (det) {
    var summary = det.querySelector('summary');
    var body = det.querySelector('.pdp-acc__body');
    if (!summary || !body) return;

    var anim = null;
    var holding = null;

    /* Hold the summary line where it is, for as long as the panel is moving.
       This is the whole of "expand down, not up". The buy panel is sticky and
       taller than the viewport, so the browser keeps its BOTTOM against the
       bottom of the screen: grow the panel by 500px and its top -- and the
       heading the customer just clicked -- slides 500px off the top of the
       screen. Measured on staging before the fix: the SPECS heading jumped from
       1012px down the viewport to 528px. A single before/after correction
       cannot catch it, because the movement happens over every frame of the
       animation, so the correction runs on every frame too. */
    function hold(ms) {
      var target = summary.getBoundingClientRect().top;
      var until = Date.now() + ms;
      if (holding) cancelAnimationFrame(holding);
      (function step() {
        var drift = summary.getBoundingClientRect().top - target;
        if (Math.abs(drift) > 0.5) window.scrollBy(0, drift);
        holding = Date.now() < until ? requestAnimationFrame(step) : null;
      })();
    }

    function clear() {
      body.style.height = '';
      anim = null;
    }

    function expand() {
      var target = summary.getBoundingClientRect().top;
      det.open = true;
      body.style.height = '0px';   /* same task as open=true, so the full-height frame is never painted */
      var full = body.scrollHeight;
      var back = summary.getBoundingClientRect().top - target;
      if (Math.abs(back) > 0.5) window.scrollBy(0, back);
      if (reducedQ.matches) { clear(); return; }
      anim = body.animate(
        [{ height: '0px' }, { height: full + 'px' }],
        { duration: DUR, easing: EASE }
      );
      anim.onfinish = clear;
      anim.oncancel = clear;
      hold(DUR + 80);
    }

    function collapse() {
      if (reducedQ.matches) { det.open = false; clear(); return; }
      var start = body.getBoundingClientRect().height;
      body.style.height = start + 'px';
      anim = body.animate(
        [{ height: start + 'px' }, { height: '0px' }],
        { duration: DUR, easing: EASE }
      );
      anim.onfinish = function () { det.open = false; clear(); };
      anim.oncancel = clear;
      hold(DUR + 80);
    }

    summary.addEventListener('click', function (e) {
      e.preventDefault();
      if (anim) { anim.cancel(); clear(); }
      if (det.open) collapse(); else expand();
    });
  });

  /* "Read more" under the one-line description: go to the description panel
     and open it (Andre, 2026-09-02).

     OPEN FIRST, SCROLL SECOND, AND ONLY IF THE SCROLL CAN ACHIEVE ANYTHING.

     Scrolling first was the obvious order and it is wrong twice over. On a
     desktop the buy panel is sticky and taller than the viewport, so it is
     already pinned at page load and its children DO NOT MOVE in the viewport
     when the page scrolls: scrollIntoView on the summary moved it 12px out of a
     requested ~400 (measured 2026-09-02). And expand() installs hold(), which
     calls window.scrollBy on every frame of the animation to keep the summary
     put, so any smooth scroll still in flight is fought frame by frame.

     So: open it, let hold() settle, then scroll only when the summary is
     actually off screen. That is a no-op on a desktop, where the panel is
     already in view and stuck, and it is the real "push down to it" on a phone,
     where the layout is stacked and nothing is sticky. */
  var more = document.querySelector('[data-pdp-readmore]');
  var panel = root.querySelector('[data-pdp-acc-description]');
  if (more && panel) {
    /* The button is a disclosure control, so it reports the panel's state. */
    panel.addEventListener('toggle', function () {
      more.setAttribute('aria-expanded', panel.open ? 'true' : 'false');
    });
    more.addEventListener('click', function () {
      var summary = panel.querySelector('summary');
      if (!summary) return;
      if (!panel.open) summary.click();
      window.setTimeout(function () {
        /* ALWAYS scroll, and to the TOP of the viewport rather than the middle
           (Andre, 2026-09-02: "when I click on it, it opens the description but
           doesn't push me down to read the description").

           The previous version skipped the scroll whenever the summary was
           already on screen, which after the nav work it usually is -- so the
           panel opened under the fold and nothing moved. And `block: 'center'`
           put the heading halfway down, leaving half a screen for a panel that
           runs ~1000px. `block: 'start'` with the scroll-margin-top on the
           <details> lands it just under the fixed header with the whole
           description below it. */
        /* Scroll the <details>, NOT the <summary>. `scroll-margin-top` applies
           to the element scrollIntoView was called on, and it is set on the
           <details>; calling this on the summary ignored it and parked the
           heading at y=0, underneath the fixed header. Their top edges are the
           same, so this only changes which element's margin is honoured. */
        panel.scrollIntoView({
          behavior: reducedQ.matches ? 'auto' : 'smooth',
          block: 'start',
        });
      }, reducedQ.matches ? 0 : DUR + 120);
    });
  }
})();
